import React, { useState, useContext, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  useTheme,
  IconButton,
  Paper,
  Avatar,
  Stack,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { ThemeContext } from "../../../../../context/ThemeContextProvider";
import { useFeatures } from "../../../../../context/PermissionsAndFeatures/FeaturesContext";
import ReservationForm from "../../ReservationFormModel/AddReservationForm";
import ReservationActionsModal from "../../ReservationFormModel/ReservationActionsModal";
import ReservationEditDialog from "../../ReservationFormModel/ReservationEditDialog";
import {
  fetchPendingReservations,
  deleteReservation,
  confirmReservation,
  cancelReservation,
  fetchCustomers,
} from "../../../../../services/pos/Tables";

const ReservationsSection = () => {
  const [selectedFloor, setSelectedFloor] = useState("1st Floor");
  const [reservations, setReservations] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [openActions, setOpenActions] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [customers, setCustomers] = useState({});

  const { mode } = useContext(ThemeContext);
  const theme = useTheme();

  const { hasFeature, featuresLoading } = useFeatures();
  const reservationItemsEnabled = !featuresLoading && hasFeature("reservation_items");

  const handleOpenForm = () => {
    if (!reservationItemsEnabled) {
      alert("🚫 ميزة إنشاء الحجوزات غير مفعلة في حسابك.");
      return;
    }
    setOpenForm(true);
  };
  const handleCloseForm = () => setOpenForm(false);

  const handleDateRangeChange = (event) => {
    const { name, value } = event.target;
    setDateRange((prev) => ({ ...prev, [name]: value }));
  };

  const handleOpenActions = (reservation) => {
    setSelectedReservation(reservation);
    setOpenActions(true);
  };

  const handleCloseActions = () => {
    setSelectedReservation(null);
    setOpenActions(false);
  };

  const handleOpenEdit = (e, reservation) => {
    e.stopPropagation();
    setSelectedReservation(reservation?.id);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setSelectedReservation(null);
  };

  const refreshReservations = () => {
    fetchPendingReservations()
      .then((data) => {
        setReservations(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error("❌ خطأ في تحديث الحجوزات:", err.message);
      });
  };

  useEffect(() => {
    fetchPendingReservations()
      .then((data) => {
        setReservations(Array.isArray(data) ? data : []);
        const customerIds = Array.isArray(data)
          ? data.map((res) => res.customer_id).filter((id) => id)
          : [];
        if (customerIds.length > 0) {
          fetchCustomers(customerIds)
            .then((customerData) => {
              const customerMap = customerData.reduce((acc, customer) => {
                acc[customer.id] = {
                  name: customer.name,
                  address1: customer.address1,
                  phone1: customer.phone1,
                };
                return acc;
              }, {});
              setCustomers(customerMap);
            })
            .catch((err) => console.error("❌ خطأ في تحميل بيانات العملاء:", err.message));
        }
      })
      .catch((err) => {
        console.error("❌ خطأ في تحميل الحجوزات:", err.message);
        setReservations([]);
      });
  }, []);

  useEffect(() => {
    localStorage.setItem("current_component", "reservations");
  }, []);

  const filteredReservations = reservations.filter((res) => {
    const reservationDate = new Date(res.reservation_date);
    const fromDate = dateRange.from ? new Date(dateRange.from) : null;
    const toDate = dateRange.to ? new Date(dateRange.to) : null;

    return (
      (!fromDate || reservationDate >= fromDate) &&
      (!toDate || reservationDate <= toDate)
    );
  });

  const formatTime12 = (time = "") => {
    if (!time) return "-";
    const [h, m] = time.split(":");
    let hour = Number(h);
    const ampm = hour >= 12 ? "م" : "ص";
    hour = hour % 12 || 12;
    return `${hour}:${m} ${ampm}`;
  };

  const computeCountdown = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return "-";
    const dt = new Date(`${dateStr}T${timeStr}`);
    const now = new Date();
    const diffMs = dt - now;
    if (diffMs <= 0) return "بدأ الحجز";
    const diffMin = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMin / 60);
    const mins = diffMin % 60;
    return hours > 0 ? `${hours} س و${mins} د` : `${mins} د`;
  };

  return (
    <Box dir="rtl" sx={{ p: 2, backgroundColor: mode === "dark" ? "#0f1220" : "#fafafa", minHeight: "100vh", pb: 16 }}>
      {/* Filters (mobile friendly) */}
      <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1, mb: 2 }}>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <TextField
            type="date"
            label="من"
            name="from"
            value={dateRange.from}
            onChange={handleDateRangeChange}
            size="small"
            sx={{
              backgroundColor: mode === "dark" ? "#12121a" : "#fff",
              borderRadius: 1,
              minWidth: 140,
            }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="date"
            label="إلى"
            name="to"
            value={dateRange.to}
            onChange={handleDateRangeChange}
            size="small"
            sx={{
              backgroundColor: mode === "dark" ? "#12121a" : "#fff",
              borderRadius: 1,
              minWidth: 140,
            }}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="outlined"
            onClick={() => setDateRange({ from: "", to: "" })}
            sx={{ minHeight: 40 }}
          >
            إعادة التهيئة
          </Button>
        </Box>

        <Box sx={{ display: "flex", gap: 1, marginLeft: "auto", mt: { xs: 1, sm: 0 } }}>
          <Button
            variant="contained"
            onClick={handleOpenForm}
            disabled={!reservationItemsEnabled}
            sx={{
              bgcolor: reservationItemsEnabled ? "#ff85b3" : "#ccc",
              color: reservationItemsEnabled ? "#000" : "#666",
              "&:hover": { bgcolor: reservationItemsEnabled ? "#ea7c69" : "#ccc" },
            }}
          >
            ➕ إضافة حجز جديد
          </Button>
        </Box>
      </Box>

      {/* Feature warning */}
      {!featuresLoading && !reservationItemsEnabled && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="error">
            🚫 ميزة إنشاء الحجوزات (reservation_items) غير مفعّلة في حسابك.
          </Typography>
        </Box>
      )}

      {/* Mobile-friendly list (cards) */}
      <Grid container spacing={2}>
        {filteredReservations.map((reservation, idx) => {
          const cust = customers[reservation.customer_id] || {};
          return (
            <Grid item xs={12} key={reservation.id}>
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                  bgcolor: mode === "dark" ? "#12121a" : "#fff",
                  cursor: "pointer",
                }}
                onClick={() => handleOpenActions(reservation)}
              >
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar sx={{ bgcolor: "#F48FB1", width: 44, height: 44 }}>{(cust.name || reservation.customer_name || "?").charAt(0)}</Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 800, fontSize: 16 }}>{cust.name || reservation.customer_name || "اسم العميل"}</Typography>
                      <Typography sx={{ fontSize: 13, color: mode === "dark" ? "#cfcff0" : "#666" }}>{cust.phone1 || reservation.customer_phone || "—"}</Typography>
                      <Typography sx={{ fontSize: 12, color: mode === "dark" ? "#9ea0b8" : "#999" }}>{cust.address1 || reservation.customer_address || "—"}</Typography>
                    </Box>
                  </Stack>

                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.7 }}>
                    <Chip
                      label={reservation.table?.name || "طاولة ؟"}
                      sx={{ bgcolor: "#1ecb6b", color: "#fff", fontWeight: 700 }}
                    />
                    <Typography sx={{ fontWeight: 700 }}>{reservation.reservation_date || "?"}</Typography>
                    <Typography sx={{ color: mode === "dark" ? "#cfcff0" : "#666" }}>{formatTime12(reservation.reservation_time || "")}</Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mt: 1 }}>
                  <Box>
                    <Typography sx={{ fontSize: 13 }}>الضيوف: <strong>{reservation.number_of_people}</strong></Typography>
                    <Typography sx={{ mt: 0.5 }}>
                      الحالة:{" "}
                      <Box component="span" sx={{
                        px: 1.2,
                        py: 0.3,
                        borderRadius: 2,
                        bgcolor: reservation.status === "confirmed" ? "#4CAF50" : reservation.status === "pending" ? "#FFC107" : reservation.status === "cancelled" ? "#F44336" : "#ddd",
                        color: reservation.status === "confirmed" ? "#fff" : "#232334",
                        fontWeight: 700,
                      }}>
                        {reservation.status === "confirmed" ? "مؤكد" : reservation.status === "pending" ? "قيد الانتظار" : reservation.status === "cancelled" ? "ملغي" : reservation.status}
                      </Box>
                    </Typography>
                  </Box>

                  <Box sx={{ textAlign: "center" }}>
                    <Typography sx={{ fontSize: 13, color: mode === "dark" ? "#cfcff0" : "#666" }}>العد التنازلي</Typography>
                    <Typography sx={{ fontWeight: 800 }}>{computeCountdown(reservation.reservation_date, reservation.reservation_time)}</Typography>
                  </Box>
                </Stack>

                {/* Actions */}
                <Stack direction="row" spacing={1} sx={{ mt: 1 }} justifyContent="flex-end">
                  <IconButton
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (window.confirm("هل أنت متأكد من تأكيد الحجز؟")) {
                        try {
                          await confirmReservation(reservation.id);
                          refreshReservations();
                        } catch (err) {
                          alert("❌ فشل في تأكيد الحجز");
                        }
                      }
                    }}
                    color="success"
                    size="small"
                  >
                    <CheckCircleIcon />
                  </IconButton>

                  <IconButton
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (window.confirm("هل أنت متأكد من إلغاء الحجز؟")) {
                        try {
                          await cancelReservation(reservation.id);
                          refreshReservations();
                        } catch (err) {
                          alert("❌ فشل في إلغاء الحجز");
                        }
                      }
                    }}
                    color="warning"
                    size="small"
                  >
                    <CancelIcon />
                  </IconButton>

                  <IconButton
                    onClick={(e) => handleOpenEdit(e, reservation)}
                    color="primary"
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>

                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!window.confirm("هل تريد حذف الحجز؟")) return;
                      deleteReservation(reservation.id)
                        .then(() => {
                          refreshReservations();
                        })
                        .catch((err) => {
                          console.error("❌ فشل في حذف الحجز:", err.message);
                        });
                    }}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Floating create button */}
      <Box sx={{ position: "fixed", bottom: 18, left: 18, zIndex: 1600 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenForm}
          disabled={!reservationItemsEnabled}
          sx={{ bgcolor: "#ff85b3", color: "#000" }}
        >
          إنشاء
        </Button>
      </Box>

      {/* Existing modals/forms (unchanged behavior, rendered as before) */}
      <ReservationForm
        open={openForm}
        onClose={handleCloseForm}
        setReservations={setReservations}
        onSuccess={refreshReservations}
      />

      <ReservationActionsModal
        open={openActions}
        onClose={handleCloseActions}
        onDelete={() => {
          if (!selectedReservation) return;
          deleteReservation(selectedReservation.id)
            .then(() => {
              refreshReservations();
              handleCloseActions();
            })
            .catch((err) => {
              console.error("❌ فشل في حذف الحجز:", err.message);
            });
        }}
      />

      <ReservationEditDialog
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        reservationId={selectedReservation}
        setReservations={(updatedReservations) => setReservations(updatedReservations)}
        reservations={reservations}
        onEdit={(updatedReservation) => {
          setReservations((prev) =>
            prev.map((res) => (res.id === updatedReservation.id ? updatedReservation : res))
          );
          handleCloseEditDialog();
        }}
      />
    </Box>
  );
};

export default ReservationsSection;
